<template>
  <div class="contact-table-container relative">
    <div :style="{ height: height }">
      <ve-table
        style="height: 100%;"
        :fixed-header="true"
        :columns="columns"
        :table-data="displayTableData"
        :cell-span-option="cellSpanOption"
        :cell-style-option="cellStyleOption"
      />
    </div>
    <div v-if="isLoading" class="contacts-loader-overlay">
      <div class="contacts-loader-content">
        <spinner size="48" />
        <span class="ml-4 font-bold text-base">{{ loadingMessage }}</span>
      </div>
    </div>
    <div
      v-else-if="!isLoading && !contacts.length && !scheduleUsesTableBodyEmpty"
      class="absolute inset-0 text-center"
    >
      <empty-state v-if="!isLabelSelected && !isReminders && !isMeetings"
>
        <h3
          class="text-slate-700 dark:text-slate-200 block text-center w-full text-3xl font-thin"
        >
          {{ emptyFilterMessage || 'Please select a label' }}
        </h3>
      </empty-state>
      <empty-state v-else-if="!isReminders && !isMeetings">
        <h3
          class="text-slate-700 dark:text-slate-200 block text-center w-full text-3xl font-thin"
        >
          {{ $t('CONTACTS_PAGE.LIST.NO_CONTACTS') }}
        </h3>
      </empty-state>
      <empty-state v-else-if="isReminders"
>
        <h3
          class="text-slate-700 dark:text-slate-200 block text-center w-full text-3xl font-thin"
        >
          {{ scheduleEmptyMessage }}
        </h3>
      </empty-state>
      <empty-state v-else
>
        <h3
          class="text-slate-700 dark:text-slate-200 block text-center w-full text-3xl font-thin"
        >
          {{ scheduleEmptyMessage }}
        </h3>
      </empty-state>
    </div>
    <div v-if="contacts.length > 0" class="table-pagination">
      <ve-pagination
        :total="meta.count || 0"
        :page-index="pageIndex"
        :page-size="pageSize"
        :page-size-option="[pageSize]"
        @on-page-number-change="onPageNumberChange"
      />
      <div class="w-full h-[1px]" />
    </div>
    <div v-else class="w-full h-[57px]" />
  </div>
</template>

<script lang="jsx">
import { h } from 'vue';
import { VeTable, VePagination } from 'vue-easytable';
import format from 'date-fns/format';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import LeadQualityCrown from 'dashboard/components/LeadQualityCrown.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon';
import rtlMixin from 'shared/mixins/rtlMixin';
import alertMixin from 'shared/mixins/alertMixin';
import isAdminMixin from 'dashboard/mixins/isAdmin';
import { parseReminderTiming } from 'dashboard/helper/reminderTimingHelper';

// Inline style constants (scoped CSS does NOT apply inside ve-table renderBodyCell)
const PAST_COLOR = '#9ca3af';
const NORMAL_COLOR = 'inherit';
const ONGOING_COLOR = '#16a34a';
const PERSON_ICON_PATH = 'M17.754 14a2.249 2.249 0 0 1 2.25 2.249v.575c0 .894-.32 1.76-.902 2.438-1.57 1.834-3.957 2.739-7.102 2.739-3.146 0-5.532-.905-7.098-2.74a3.75 3.75 0 0 1-.898-2.435v-.577a2.249 2.249 0 0 1 2.249-2.25h11.501Zm0 1.5H6.253a.749.749 0 0 0-.75.749v.577c0 .536.192 1.054.54 1.461 1.253 1.468 3.219 2.214 5.957 2.214s4.706-.746 5.962-2.214a2.25 2.25 0 0 0 .541-1.463v-.575a.749.749 0 0 0-.749-.75ZM12 2.004a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z';

export default {
  name: 'ContactTable',
  components: {
    EmptyState,
    Spinner,
    VeTable,
    VePagination,
    LeadQualityCrown,
    Thumbnail,
  },
  mixins: [rtlMixin, alertMixin, isAdminMixin],
  props: {
    contacts: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => ({ count: 0, currentPage: 1, perPage: 15 }),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '320px',
    },
    pageIndex: {
      type: Number,
      default: 1,
    },
    isReminders: {
      type: Boolean,
      default: false,
    },
    isMeetings: {
      type: Boolean,
      default: false,
    },
    showScheduleToolbar: {
      type: Boolean,
      default: false,
    },
    isLabelSelected: {
      type: Object,
      default: false,
    },
    /** When no rows and no filter selected yet (Priority Leads). Parent can override e.g. segment prompt. */
    emptyFilterMessage: {
      type: String,
      default: '',
    },
    onClickContact: {
      type: Function,
      default: () => {},
    },
    selectedDateRange: {
      type: Object,
      default: () => ({ id: 'next_7_days', name: 'Next 7 days' }),
    },
    /** When true, merged list includes today's completed rows (Load completed was used). */
    showCompletedInList: {
      type: Boolean,
      default: false,
    },
    /** Count of today's completed meetings/reminders from API (past.total_count; past bucket is today-only). */
    completedTodayCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    canManageReminders() {
      return this.isAdmin;
    },
    scheduleEmptyMessage() {
      if (!this.isMeetings && !this.isReminders) return '';
      const completed = Number(this.completedTodayCount) || 0;
      const showingCompleted = this.showCompletedInList;

      if (this.isMeetings) {
        if (showingCompleted) {
          return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.NO_MEETINGS_INCLUDING_COMPLETED_TODAY');
        }
        if (completed > 0) {
          return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.COMPLETED_MEETINGS_FOR_TODAY_COUNT', {
            count: completed,
          });
        }
        return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.NO_MEETINGS_FOR_RANGE', {
          range: this.dateRangeText,
        });
      }

      if (showingCompleted) {
        return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.NO_REMINDERS_INCLUDING_COMPLETED_TODAY');
      }
      if (completed > 0) {
        return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.COMPLETED_REMINDERS_FOR_TODAY_COUNT', {
          count: completed,
        });
      }
      return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.NO_REMINDERS_FOR_RANGE', {
        range: this.dateRangeText,
      });
    },
    scheduleUsesTableBodyEmpty() {
      return this.scheduleTableSpanEnabled;
    },
    scheduleTableSpanEnabled() {
      return this.showScheduleToolbar && (this.isMeetings || this.isReminders);
    },
    displayTableData() {
      if (!this.scheduleTableSpanEnabled) {
        return this.tableData;
      }
      const toolbarRow = { id: '__schedule_toolbar__', __isScheduleToolbar: true };
      const rows = [toolbarRow, ...this.tableData];
      if (this.tableData.length === 0) {
        rows.push({ id: '__schedule_empty__', __isScheduleEmpty: true });
      }
      return rows;
    },
    cellSpanOption() {
      if (!this.scheduleTableSpanEnabled) {
        return undefined;
      }
      return {
        bodyCellSpan: ({ row, column }) => this.scheduleBodyCellSpan(row, column),
      };
    },
    cellStyleOption() {
      return {
        bodyCellClass: ({ row }) => {
          if (row && row.demoNotCompleted) {
            return 'demo-not-completed-cell';
          }
          return '';
        },
      };
    },
    loadingMessage() {
      if (this.isMeetings) return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.LOADING_MEETINGS');
      if (this.isReminders) return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.LOADING_REMINDERS');
      return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.LOADING_CONTACTS');
    },
    pageSize() {
      return this.meta.perPage || this.meta.per_page || 15;
    },
    dateRangeText() {
      if (!this.selectedDateRange) return 'the selected period';
      switch (this.selectedDateRange.id) {
        case 'today':
          return 'today';
        case 'next_7_days':
          return 'the next 7 days';
        case 'next_30_days':
          return 'the next 30 days';
        case 'next_3_months':
          return 'the next 3 months';
        case 'next_6_months':
          return 'the next 6 months';
        case 'custom':
          return 'the selected date range';
        default:
          return 'the selected period';
      }
    },
    tableData() {
      return this.contacts.map(contact => {
        let note; let time; let meetingLink; let isPast = false;
        let isOngoing = false;

        if (this.isReminders) {
          note = contact?.additional_attributes?.reminder_note || '—';
          time = this.formatDateTime(contact?.additional_attributes?.calendar_timings);
          isPast = this.isTimingInPast(contact?.additional_attributes?.calendar_timings);
        } else if (this.isMeetings) {
          note = contact?.additional_attributes?.demo_subject || '—';
          time = this.formatMeetingDateTime(
            contact?.additional_attributes?.demo_timing ||
              contact?.meeting_starts_at
          );
          meetingLink = contact?.additional_attributes?.meeting_link || '';
          const startsAt = contact?.meeting_starts_at;
          const endsAt = contact?.meeting_ends_at;
          if (startsAt && endsAt) {
            const startMs = new Date(startsAt).getTime();
            const endMs = new Date(endsAt).getTime();
            if (!Number.isNaN(startMs) && !Number.isNaN(endMs)) {
              const now = Date.now();
              isOngoing = now >= startMs && now < endMs;
              isPast = now >= endMs;
            } else {
              isPast = this.isMeetingInPast(contact?.additional_attributes?.demo_timing);
            }
          } else {
            isPast = this.isMeetingInPast(contact?.additional_attributes?.demo_timing);
          }
        }

        const board = contact?.board_name || '—';
        const status = contact?.status_name || '—';

        const rescheduleUrl =
          contact?.additional_attributes?.reschedule_url ||
          contact?.additional_attributes?.rescheduleUrl ||
          '';

        return {
          id: contact.primary_contact_meeting_id
            ? `${contact.id}-m-${contact.primary_contact_meeting_id}`
            : contact.id,
          contactId: contact.id,
          primaryContactMeetingId: contact.primary_contact_meeting_id || null,
          comapany: contact?.additional_attributes?.company_name || '—',
          name: contact.name || '—',
          email: contact.email || '—',
          phone: contact.phone_number || '—',
          thumbnail: contact.thumbnail || contact.avatar_url || '',
          note,
          time,
          createdAt: contact?.created_at,
          board,
          status,
          meetingLink,
          rescheduleUrl,
          isPast,
          isOngoing,
          demoNotCompleted:
            this.isMeetings && isPast && !!contact.demo_not_completed,
          meetingStatusRecorded:
            this.isMeetings && !!contact.demo_status_recorded,
          bookedByAvatar: contact?.booked_by_avatar || contact?.additional_attributes?.booked_by_avatar_url || '',
          bookedByName: contact?.booked_by_name || contact?.additional_attributes?.booked_by_user_name || '',
          bookedByActorType: contact?.booked_by_actor_type || contact?.additional_attributes?.booked_by_actor_type || '',
          reminderSetByAvatar: contact?.reminder_set_by_avatar || '',
          reminderSetByName: contact?.reminder_set_by_name || contact?.additional_attributes?.reminder_set_by_user_name || '',
          lead_quality_metadata: contact?.lead_quality_metadata || null,
          additional_attributes: contact?.additional_attributes || {},
          sourceContact: contact,
        };
      });
    },
    columns() {
      void this.scheduleEmptyMessage;
      void this.showCompletedInList;
      void this.canManageReminders;
      return [
        // Agent avatar for meetings (booker, else assignee); reminder author for reminders
        ...((this.isMeetings || this.isReminders) ? [{
          field: 'bookerAvatar',
          key: 'bookerAvatar',
          title: '',
          width: 50,
          align: 'center',
          renderBodyCell: ({ row }) => {
            if (row.__isScheduleToolbar) {
              return h(
                'div',
                { class: 'schedule-toolbar-row' },
                this.$slots['schedule-toolbar'] || []
              );
            }
            if (row.__isScheduleEmpty) {
              return h('div', { class: 'schedule-empty-row' }, [
                h(
                  'h3',
                  {
                    class:
                      'text-slate-700 dark:text-slate-200 w-full text-3xl font-thin m-0',
                  },
                  this.scheduleEmptyMessage
                ),
              ]);
            }
            if (this.isMeetings) {
              const agentName = row.bookedByName || '';
              const tooltip = agentName ? `Booked by: ${agentName}` : '';
              if (row.bookedByActorType === 'AgentBot') {
                return h('div', {
                  style: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'visible',
                  },
                }, [
                  h('div', {
                    style: {
                      width: '28px',
                      height: '28px',
                      minWidth: '28px',
                      minHeight: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#e8ecf4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #40518A',
                      color: '#40518A',
                    },
                    directives: tooltip ? [{ name: 'tooltip', value: tooltip }] : [],
                  }, [
                    h(FluentIcon, { props: { icon: 'bot', size: '16' } }),
                  ]),
                ]);
              }
              return h('div', {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'visible',
                },
                directives: tooltip ? [{ name: 'tooltip', value: tooltip }] : [],
              }, [
                h(Thumbnail, {
                  props: {
                    src: row.bookedByAvatar,
                    size: '28px',
                    username: agentName,
                  },
                }),
              ]);
            }
            const avatarUrl = row.reminderSetByAvatar;
            const tooltipName = row.reminderSetByName
              ? `Set by: ${row.reminderSetByName}`
              : '';
            if (avatarUrl) {
              return h('div', {
                style: { display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'visible' },
              }, [
                h('img', {
                  attrs: { src: avatarUrl, alt: tooltipName },
                  style: {
                    width: '28px', height: '28px', minWidth: '28px', minHeight: '28px',
                    borderRadius: '50%', objectFit: 'cover',
                    border: '2px solid #40518A',
                    display: 'block',
                  },
                  directives: tooltipName ? [{ name: 'tooltip', value: tooltipName }] : [],
                }),
              ]);
            }
            return h('div', {
              style: { display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'visible' },
            }, [
              h('div', {
                style: {
                  width: '28px', height: '28px', minWidth: '28px', minHeight: '28px',
                  borderRadius: '50%', backgroundColor: '#e8ecf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #40518A',
                },
                directives: tooltipName ? [{ name: 'tooltip', value: tooltipName }] : [],
              }, [
                h('svg', {
                  attrs: { width: '14', height: '14', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
                  style: { display: 'block' },
                }, [
                  h('path', { attrs: { d: PERSON_ICON_PATH, fill: '#40518A' } })
                ])
              ]),
            ]);
          },
        }] : []),
        // NAME column
        {
          field: 'name',
          key: 'name',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          // Priority Leads gives up 20px so Created At can grow; the 13-char
          // ellipsis + tooltip on overflow keeps long names readable.
          // Meetings/Reminders keeps its original 156 (no Created At column there).
          width: (this.isMeetings || this.isReminders) ? 156 : 120,
          renderBodyCell: ({ row }) => {
            if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
              return h('span');
            }
            const nameText = row.name.length > 13
              ? `${row.name.charAt(0).toUpperCase() + row.name.slice(1, 13)}...`
              : row.name.charAt(0).toUpperCase() + row.name.slice(1);

            const nameSpan = h('span', {
              style: {
                ...this.rowTextStyle(row),
                fontSize: '13px',
                fontWeight: '500',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                minWidth: 0,
                flex: '1 1 auto',
              },
              directives: row.name.length > 13 ? [{ name: 'tooltip', value: row.name }] : [],
            }, nameText);

            const isSchedule = this.isMeetings || this.isReminders;
            if (!isSchedule) {
              // Priority Leads / default: gold crown only (LeadQualityCrown default; no muted-below-threshold).
              return h('div', {
                style: {
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  minWidth: 0,
                  flexDirection: this.isRTLView ? 'row-reverse' : 'row',
                },
                on: { click: () => this.onClickContact(row.contactId || row.id) },
              }, [
                h(LeadQualityCrown, {
                  props: {
                    accountId: this.$route.params.accountId,
                    metadata: row.lead_quality_metadata,
                    tooltipPlacement: this.isRTLView ? 'left' : 'right',
                  },
                }),
                nameSpan,
              ]);
            }

            return h('div', {
              style: {
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minWidth: 0,
                flexDirection: this.isRTLView ? 'row-reverse' : 'row',
              },
              on: { click: () => this.onClickContact(row.contactId || row.id) },
            }, [
              h(LeadQualityCrown, {
                props: {
                  accountId: this.$route.params.accountId,
                  metadata: row.lead_quality_metadata,
                  tooltipPlacement: this.isRTLView ? 'left' : 'right',
                },
              }),
              nameSpan,
            ]);
          },
        },
        // COMPANY column
        {
          field: 'comapany',
          key: 'comapany',
          title: 'Company',
          // Same trade as the Name column: shrink for Priority Leads so the
          // Created At column has room to render the full date+time without
          // ellipsis; keep 120 for Meetings/Reminders where the row is laid
          // out differently.
          width: (this.isMeetings || this.isReminders) ? 120 : 100,
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
              return h('span');
            }
            if (row.comapany) {
              return h('div', {
                style: {
                  ...this.rowTextStyle(row),
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                },
                directives: row.comapany.length > 15 ? [{ name: 'tooltip', value: row.comapany }] : [],
              }, row.comapany);
            }
            return '---';
          },
        },
        // Schedule columns (meetings or reminders)
        ...((this.isReminders || this.isMeetings)
          ? [
              // SUBJECT / NOTES column
              {
                field: 'note',
                key: 'note',
                title: this.isMeetings ? 'Subject' : 'Notes',
                width: 120,
                align: this.isRTLView ? 'right' : 'left',
                renderBodyCell: ({ row }) => {
                  if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
                    return h('span');
                  }
                  if (row.note && row.note !== '—') {
                    return h('div', {
                      style: {
                        ...this.rowTextStyle(row),
                        cursor: 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      },
                      directives: [{ name: 'tooltip', value: row.note }],
                    }, row.note);
                  }
                  return '—';
                },
              },
              // TIME column
              {
                field: 'time',
                key: 'time',
                title: 'Time',
                width: 140,
                align: this.isRTLView ? 'right' : 'left',
                renderBodyCell: ({ row }) => {
                  if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
                    return h('span');
                  }
                  if (row.time && row.time !== '---') {
                    return h('div', {
                      style: { ...this.rowTextStyle(row), fontSize: '13px' },
                    }, row.time);
                  }
                  return '—';
                },
              },
              // ACTION column: meetings always; reminders only for admins (agents have no actions)
              ...((this.isMeetings || (this.isReminders && this.canManageReminders)) ? [{
                field: 'meetingLink',
                key: 'meetingLink',
                title: 'Action',
                width: 100,
                align: 'center',
                renderBodyCell: ({ row }) => {
                  if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
                    return h('span');
                  }
                  if (this.isMeetings) {
                    const isPastMeeting = row.isPast && !row.isOngoing;
                    const showScheduleActions = !isPastMeeting;
                    const showMeetingStatus =
                      isPastMeeting && !!row.primaryContactMeetingId;
                    if (!(row.meetingLink || showScheduleActions || showMeetingStatus)) {
                      return '—';
                    }
                    const children = [];
                    if (row.meetingLink) {
                      children.push(
                        h('woot-button', {
                          attrs: {
                            icon: 'copy',
                            size: 'tiny',
                            variant: 'smooth',
                            'color-scheme': isPastMeeting ? 'secondary' : 'primary',
                          },
                          directives: [
                            { name: 'tooltip', value: 'Copy meeting link' },
                          ],
                          on: { click: () => this.copyMeetingLink(row.meetingLink) },
                        })
                      );
                    }
                    if (showScheduleActions) {
                      children.push(
                        h('woot-button', {
                          attrs: {
                            icon: 'arrow-up-right',
                            size: 'tiny',
                            variant: 'smooth',
                            'color-scheme': 'primary',
                          },
                          directives: [
                            {
                              name: 'tooltip',
                              value: {
                                content: this.$t(
                                  'OVERVIEW_REPORTS.PRIORITY_LEADS.KIVO_RESCHEDULE_CANCEL_TOOLTIP'
                                ),
                                classes: [
                                  'vue-tooltip-theme',
                                  'tooltip-preline',
                                ],
                              },
                            },
                          ],
                          on: {
                            click: () =>
                              this.$emit(
                                'reschedule-meeting',
                                row.sourceContact || row
                              ),
                          },
                        }),
                        h('woot-button', {
                          attrs: {
                            icon: 'delete',
                            size: 'tiny',
                            variant: 'smooth',
                            'color-scheme': 'alert',
                          },
                          directives: [
                            {
                              name: 'tooltip',
                              value: this.$t('EDIT_CONTACT.CANCEL_MEETING'),
                            },
                          ],
                          on: {
                            click: () =>
                              this.$emit(
                                'cancel-meeting',
                                row.sourceContact || row
                              ),
                          },
                        })
                      );
                    }
                    if (showMeetingStatus) {
                      const statusRecorded = !!row.meetingStatusRecorded;
                      children.push(
                        h('woot-button', {
                          class: statusRecorded
                            ? 'meeting-status-recorded-btn'
                            : '',
                          attrs: {
                            icon: statusRecorded ? 'edit' : 'checkmark',
                            size: 'tiny',
                            variant: 'smooth',
                            'color-scheme': statusRecorded ? 'success' : 'secondary',
                          },
                          directives: [
                            {
                              name: 'tooltip',
                              value: statusRecorded
                                ? this.$t(
                                    'OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_EDIT_ACTION'
                                  )
                                : this.$t(
                                    'OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_ACTION'
                                  ),
                            },
                          ],
                          on: {
                            click: () =>
                              this.$emit(
                                'meeting-status',
                                row.sourceContact || row
                              ),
                          },
                        })
                      );
                    }
                    return h(
                      'div',
                      {
                        style: {
                          display: 'flex',
                          gap: '4px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                        },
                      },
                      children
                    );
                  }

                  // Reminders: update + complete — admins only
                  const hasReminder =
                    !!row.additional_attributes?.calendar_timings ||
                    !!row.additional_attributes?.reminder_note;
                  if (!hasReminder) {
                    return '—';
                  }
                  return h(
                    'div',
                    {
                      style: {
                        display: 'flex',
                        gap: '4px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                      },
                    },
                    [
                      h('woot-button', {
                        attrs: {
                          icon: 'edit',
                          size: 'tiny',
                          variant: 'smooth',
                          'color-scheme': 'primary',
                        },
                        directives: [
                          {
                            name: 'tooltip',
                            value: this.$t(
                              'OVERVIEW_REPORTS.PRIORITY_LEADS.UPDATE_REMINDER'
                            ),
                          },
                        ],
                        on: {
                          click: () =>
                            this.$emit('update-reminder', row.sourceContact || row),
                        },
                      }),
                      this.renderReminderCompleteCheckbox(row),
                    ]
                  );
                },
              }] : []),
            ]
          // Default columns (hot leads / other)
          : [
              {
                field: 'board',
                key: 'board',
                title: 'Board/Status',
                width: 150,
                align: this.isRTLView ? 'right' : 'left',
                renderBodyCell: ({ row }) => {
                  if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
                    return h('span');
                  }
                  if (row.board) {
                    const boardText = row.board.length > 8 ? row.board.slice(0, 13) + '..' : row.board;
                    const statusText = row.status.length > 8 ? row.status.slice(0, 13) + '..' : row.status;
                    const fullText = `${row.board}/${row.status}`;
                    return h('div', {
                      class: 'break-all text-left text-[14px] overflow-hidden whitespace-nowrap text-ellipsis',
                      directives: (row.board.length + row.status.length) > 20
                        ? [{ name: 'tooltip', value: fullText }]
                        : [],
                    }, `${boardText}/${statusText}`);
                  }
                  return '---';
                },
              },
              {
                field: 'createdAt',
                key: 'createdAt',
                title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.CREATED_AT'),
                width: 140,
                align: this.isRTLView ? 'right' : 'left',
                renderBodyCell: ({ row }) => {
                  if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
                    return h('span');
                  }
                  const formatted = this.formatDateTime(row.createdAt);
                  if (formatted && formatted !== '---') {
                    // 11px keeps the full date-fns "PP p" output ("May 28, 2026,
                    // 6:17 PM") on a single line inside the 140px column.
                    return h('div', {
                      style: {
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      directives: [{ name: 'tooltip', value: formatted }],
                    }, formatted);
                  }
                  return '—';
                },
              },
           ]),
      ];
    },
  },
  methods: {
    rowTextColor(row) {
      if (row.isOngoing) return ONGOING_COLOR;
      if (row.isPast) return PAST_COLOR;
      return NORMAL_COLOR;
    },
    rowTextStyle(row) {
      return {
        color: this.rowTextColor(row),
      };
    },
    scheduleBodyCellSpan(row, column) {
      const cols = this.columns;
      if (!cols?.length) return { rowspan: 1, colspan: 1 };
      const firstKey = cols[0].key;
      if (row.__isScheduleToolbar || row.__isScheduleEmpty) {
        if (column.key === firstKey) {
          return { rowspan: 1, colspan: cols.length };
        }
        return { rowspan: 0, colspan: 0 };
      }
      return { rowspan: 1, colspan: 1 };
    },
    onPageNumberChange(pageIndex) {
      this.$emit('page-change', pageIndex);
    },
    formatDateTime(value) {
      if (!value) return '---';
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      try {
        return format(d, 'PP p');
      } catch (e) {
        return d.toLocaleString();
      }
    },
    formatMeetingDateTime(value) {
      if (!value) return '---';
      try {
        const d = parseReminderTiming(value);
        if (d) return format(d, 'PP p');
        return String(value).split(',').slice(0, 3).join(',').trim();
      } catch (e) {
        return value;
      }
    },
    isMeetingInPast(value) {
      if (!value) return false;
      try {
        const d = parseReminderTiming(value);
        return d ? d < new Date() : false;
      } catch (e) {
        return false;
      }
    },
    isTimingInPast(value) {
      if (!value) return false;
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return false;
        return d < new Date();
      } catch (e) {
        return false;
      }
    },
    openKivoRescheduleUrl(url) {
      const trimmed = (url || '').trim();
      if (!trimmed) return;
      window.open(trimmed, '_blank', 'noopener,noreferrer');
    },
    async copyMeetingLink(link) {
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
        this.showAlert('Meeting link copied to clipboard');
      } catch (err) {
        console.error('Failed to copy meeting link:', err);
      }
    },
    checkboxIconAttrs() {
      return {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        width: '20',
        height: '20',
        'aria-hidden': 'true',
        focusable: 'false',
      };
    },
    renderEmptyCheckboxIcon() {
      return h('svg', { attrs: this.checkboxIconAttrs() }, [
        h('rect', {
          attrs: {
            x: '4.5',
            y: '4.5',
            width: '15',
            height: '15',
            rx: '3',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '1.5',
          },
        }),
      ]);
    },
    renderReminderCompleteCheckbox(row) {
      const completeLabel = this.$t(
        'OVERVIEW_REPORTS.PRIORITY_LEADS.COMPLETE_REMINDER'
      );

      const checkbox = h(
        'button',
        {
          class: 'daily-work-status-checkbox',
          attrs: {
            type: 'button',
            'aria-label': completeLabel,
          },
          directives: [
            {
              name: 'tooltip',
              value: {
                content: completeLabel,
                classes: ['vue-tooltip-theme', 'tooltip-preline'],
              },
            },
          ],
          on: {
            click: event => {
              event.stopPropagation();
              this.$emit('complete-reminder', row.sourceContact || row);
            },
          },
        },
        [this.renderEmptyCheckboxIcon()]
      );

      return h('div', { class: 'daily-work-status-wrap' }, [checkbox]);
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-table-container {
  @apply flex flex-col flex-1;
  position: relative;

  .schedule-toolbar-row {
    display: flex;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    padding: 8px var(--space-two);
    background: var(--s-25);
    border-bottom: 1px solid var(--s-100);
  }

  :global(.dark) .schedule-toolbar-row,
  :global([data-theme='dark']) .schedule-toolbar-row {
    background: var(--s-800);
    border-color: var(--s-600);
  }

  :global(.dark) .ve-table :deep(td.ve-table-body-td.demo-not-completed-cell),
  :global([data-theme='dark']) .ve-table :deep(td.ve-table-body-td.demo-not-completed-cell) {
    background-color: rgba(254, 226, 226, 0.16) !important;
  }

  /* WootButton renders <button class="button ...">, not .woot-button; __content is w-full + text-left (small) */
  .schedule-toolbar-row :deep(.button) {
    width: 100%;
    justify-content: center;
  }

  .schedule-toolbar-row :deep(.button .button__content) {
    @apply text-center;
    text-align: center !important;
  }

  .schedule-empty-row {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: var(--space-normal) var(--space-two);
    text-align: center;
  }

  .ve-table {
    &:deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }

      td.ve-table-body-td.demo-not-completed-cell {
        background-color: #fee2e2 !important;
      }

      /* Reduce padding on the avatar column (first col) so icon doesn't clip */
      col:first-child + col,
      td.ve-table-body-td:first-child,
      th.ve-table-header-th:first-child {
        padding: 4px !important;
        overflow: visible !important;
      }
    }
  }

  &:deep(.ve-pagination) {
    @apply bg-transparent dark:bg-transparent;
  }

  &:deep(.ve-pagination-select) {
    @apply hidden;
  }

  .row-user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col min-w-0 my-0 mx-2;

      .title {
        @apply text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100;
      }
      .sub-title {
        @apply text-xs text-slate-600 dark:text-slate-200;
      }
    }
  }

  .table-pagination {
    @apply mt-4 text-right;
  }

  :deep(.daily-work-status-wrap) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.daily-work-status-checkbox) {
    @apply inline-flex items-center justify-center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
  }

  :deep(.daily-work-status-checkbox svg) {
    display: block;
  }

  :deep(.daily-work-status-checkbox:hover) {
    color: #64748b;
  }

  :deep(.daily-work-status-checkbox:disabled) {
    opacity: 0.45;
    cursor: default;
  }

  :deep(.meeting-status-recorded-btn.button) {
    background-color: #f1f5f9 !important;
    border-color: #e2e8f0 !important;
    color: #475569 !important;
  }

  :deep(.meeting-status-recorded-btn.button:hover) {
    background-color: #e2e8f0 !important;
    color: #334155 !important;
  }

  :global(.dark) :deep(.meeting-status-recorded-btn.button),
  :global([data-theme='dark']) :deep(.meeting-status-recorded-btn.button) {
    background-color: rgba(148, 163, 184, 0.15) !important;
    border-color: rgba(148, 163, 184, 0.35) !important;
    color: #cbd5e1 !important;
  }
}

.contacts-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(2px);

  :global(.dark) &,
  :global([data-theme='dark']) & {
    background: rgba(0, 0, 0, 0.92);
  }
}

.contacts-loader-content {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style lang="scss">
/* Row is rendered inside vue-easytable; unscoped ensures centering applies */
.schedule-toolbar-row .button {
  width: 100%;
  justify-content: center;
}

.schedule-toolbar-row .button .button__content {
  text-align: center !important;
}
</style>
